/**
 * Accounting-document PDF download helpers — invoices and credit notes.
 *
 * The backend serves the documents inline (`GET …/invoice`, `GET
 * …/credit-notes/:id`) and names the file via `Content-Disposition` — but a
 * `blob:` object URL keeps no name, so a viewer's save dialog would default to
 * `document.pdf`. On web the blob is therefore saved through a `download`
 * anchor carrying the server file name; on native it is written to the cache
 * directory and handed to the system share sheet.
 */

import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { TicketPurchaseApi } from "../services/TicketPurchaseApi";

/** File name used while the invoice number is not known yet. */
const INVOICE_FALLBACK_FILE_NAME = "facture";

/** File name used while the credit note number is not known yet. */
const CREDIT_NOTE_FALLBACK_FILE_NAME = "note-de-credit";

/** Delay before the web object URL is revoked — the new tab must have loaded it first. */
const OBJECT_URL_REVOKE_DELAY_MS = 60_000;

/**
 * Converts a Blob to the base64 payload expected by `Filesystem.writeFile`.
 *
 * @param blob - PDF blob fetched from the API.
 * @returns Base64 string without the `data:` prefix.
 */
const blobToBase64 = ( blob: Blob ): Promise<string> => {
    return new Promise( ( resolve, reject ) => {
        const reader = new FileReader();
        reader.onerror = () => reject( new Error( reader.error?.message ?? "Blob read failed" ) );
        reader.onload = () => {
            const dataUrl = typeof reader.result === "string" ? reader.result : "";
            const commaIndex = dataUrl.indexOf( "," );
            resolve( commaIndex >= 0 ? dataUrl.slice( commaIndex + 1 ) : dataUrl );
        };
        reader.readAsDataURL( blob );
    } );
};

/**
 * Saves the blob through an attached `download` anchor — the only way a blob
 * keeps its file name in the browser save dialog. The object URL is revoked
 * after a delay so the click has been consumed first.
 *
 * @param blob - PDF blob.
 * @param fileName - Download file name.
 */
const downloadBlobOnWeb = ( blob: Blob, fileName: string ): void => {
    const url = URL.createObjectURL( blob );
    const anchor = document.createElement( "a" );

    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild( anchor );
    anchor.click();
    anchor.remove();

    setTimeout( () => URL.revokeObjectURL( url ), OBJECT_URL_REVOKE_DELAY_MS );
};

/**
 * Hands a fetched document blob to the platform's document handler — the web
 * `download` anchor or the native share sheet.
 *
 * @param blob - PDF blob fetched from the API.
 * @param fileName - Download file name (server-provided or fallback).
 * @returns `true` — always resolves after the hand-off.
 */
const handOffBlob = async( blob: Blob, fileName: string ): Promise<boolean> => {
    if ( Capacitor.isNativePlatform() ) {
        const base64 = await blobToBase64( blob );
        const written = await Filesystem.writeFile( {
            "data":      base64,
            "directory": Directory.Cache,
            "path":      fileName,
        } );

        await Share.share( {
            "title": fileName,
            "url":   written.uri,
        } );

        return true;
    }

    downloadBlobOnWeb( blob, fileName );

    return true;
};

/**
 * Downloads the invoice PDF of a booking and saves it.
 *
 * Web: `download` anchor carrying the server file name. Native: the PDF is
 * written to the cache directory and the system share sheet opens on it.
 *
 * @param bookingId - FlowRide booking id (`BookingResponseDTO.bookingId`).
 * @param invoiceNumber - Invoice number (`FW-YYYY-NNNNNN`) for the file name.
 * @returns `true` when the PDF was fetched and handed over, `false` on failure.
 */
export const openInvoicePdf = async( bookingId: string, invoiceNumber?: string | null ): Promise<boolean> => {
    const fallbackName = `${ invoiceNumber ?? INVOICE_FALLBACK_FILE_NAME }.pdf`;

    try {
        const { blob, fileName } = await TicketPurchaseApi.getPurchaseInvoice( bookingId );

        return await handOffBlob( blob, fileName ?? fallbackName );
    } catch {
        return false;
    }
};

/**
 * Downloads a credit note PDF of a booking and saves it — the separate
 * accounting document of a refund (the invoice itself is never re-written).
 *
 * Same hand-off as {@link openInvoicePdf}: `download` anchor on web, share
 * sheet on native.
 *
 * @param bookingId - FlowRide booking id (`BookingResponseDTO.bookingId`).
 * @param creditNoteId - Credit note id (`CreditNoteSummaryDTO.id`).
 * @param creditNoteNumber - Credit note number (`FW-CN-YYYY-NNNNNN`) for the file name.
 * @returns `true` when the PDF was fetched and handed over, `false` on failure.
 */
export const openCreditNotePdf = async( bookingId: string, creditNoteId: string, creditNoteNumber?: string | null ): Promise<boolean> => {
    const fallbackName = `${ creditNoteNumber ?? CREDIT_NOTE_FALLBACK_FILE_NAME }.pdf`;

    try {
        const { blob, fileName } = await TicketPurchaseApi.getPurchaseCreditNote( bookingId, creditNoteId );

        return await handOffBlob( blob, fileName ?? fallbackName );
    } catch {
        return false;
    }
};
